import React, { useState, useEffect } from "react";
import {
  Typography,
  Grid,
  Box,
  Button,
  Divider,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import Header from "../../Header/Header";
import CustomerSelector from "../../Common/CustomerSelector";
import SiteSelector from "../../Common/SiteSelector";
import { CustomerDTO, SiteDTO } from "@beable/roster-backend-client-v1";
import { getStyles } from "../styles";
import CourseSetDialog from "./Dialog/CourseSetDialog";
import LocationSelector from "../../Common/Selectors/LocationSelector";
import { CourseSetDTO, LocationDTO } from "@beable/roster-backend-client-v1";
import CourseSetTable from "./CourseSetTable";
import courseService from "../../../services/CourseService";
import customerService from "../../../services/CustomerService";
import { SubHeader } from "../../Common/Layout/SubHeader";
import { BasePaper } from "../../Common/Layout/BasePaper";

export default function CourseSets() {
  const theme = useTheme();
  const styles = getStyles(theme);
  const [customer, setCustomer] = useState<CustomerDTO | undefined>(undefined);
  const [site, setSite] = useState<SiteDTO | undefined>(undefined);
  const [location, setLocation] = useState<LocationDTO | undefined>(undefined);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCourseSet, setSelectedCourseSet] = useState<
    CourseSetDTO | undefined
  >(undefined);
  const [courseSets, setCourseSets] = useState<CourseSetDTO[]>([]);
  const [sites, setSites] = useState<SiteDTO[]>([]);

  useEffect(() => {
    const fetchCourseSets = async () => {
      setCourseSets([]);

      const data = await courseService.getCourseSets(
        location?.isoCode,
        site?.id
      );
      if (data) setCourseSets(data.courseSets);
    };

    const fetchSites = async () => {
      const data = await customerService.getSites();
      if (data) setSites(data);
    };

    fetchCourseSets();
    fetchSites();
  }, [site, location]);

  const handleEditCourseSet = async (courseSetId: string) => {
    const courseSet = await courseService.getCourseSet(courseSetId);
    if (courseSet) setSelectedCourseSet(courseSet);
    setIsModalOpen(true);
  };

  const handleDialogClose = async () => {
    setSelectedCourseSet(undefined);
    setIsModalOpen(false);

    const data = await courseService.getCourseSets(location?.isoCode, site?.id);
    if (data) setCourseSets(data.courseSets);
  };

  const getCourseSetCustomerId = (siteId?: number) => {
    const site = sites.find((s) => s.id === siteId);
    if (site) return site.customerId;
  };

  return (
    <Box sx={styles.root}>
      <Box sx={styles.content}>
        <Header />
        <SubHeader>
          <Typography variant="h6" component="span">
            Course Sets
          </Typography>
        </SubHeader>
        <Grid container sx={{
          justifyContent: "center"
        }}>
          <Grid size={10}>
            <BasePaper>
              <Grid container spacing={3} sx={{
                alignItems: "center"
              }} >
                <Grid size={1} />
                <Grid size={3}>
                  <LocationSelector
                    locationCode={location?.isoCode}
                    onChange={(location: LocationDTO) => {
                      setLocation(location);
                    }}
                    allOption={true}
                    variant="outlined"
                  />
                </Grid>
                <Grid size={3}>
                  <CustomerSelector
                    locationCode={location?.isoCode}
                    onSelect={(customer: CustomerDTO) => setCustomer(customer)}
                    variant="outlined"
                    hideSearch
                    ignoreState
                  />
                </Grid>
                <Grid size={3}>
                  <SiteSelector
                    customerId={customer ? customer.id : ""}
                    onSelect={(site: SiteDTO) => setSite(site)}
                    variant={"outlined"}
                    disabled={!customer}
                    allOption
                    ignoreState
                  />
                </Grid>
              </Grid>
              <Divider />
              <Grid container sx={styles.grow} spacing={1}>
                <CourseSetTable
                  courseSets={courseSets}
                  sites={sites}
                  handleEditCourseSet={handleEditCourseSet}
                />
                <Grid container sx={{
                  justifyContent: "flex-end"
                }}>
                  <Button
                    variant="contained"
                    color="primary"
                    sx={styles.margin1}
                    aria-label="add new course set"
                    title="Add New Course Set"
                    onClick={() => setIsModalOpen(true)}
                  >
                    Add New
                  </Button>
                </Grid>
                {isModalOpen && (
                  <CourseSetDialog
                    open={isModalOpen}
                    handleClose={handleDialogClose}
                    locationCode={
                      selectedCourseSet
                        ? selectedCourseSet.locationCode
                        : location?.isoCode
                    }
                    customerId={
                      selectedCourseSet
                        ? getCourseSetCustomerId(selectedCourseSet.siteId)
                        : customer?.id
                    }
                    siteId={
                      selectedCourseSet ? selectedCourseSet.siteId : site?.id
                    }
                    courseSet={selectedCourseSet}
                  />
                )}
              </Grid>
            </BasePaper>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}
